import {
  ADVICE_NAMES,
  PurchaseRecommendation,
  SinglePayment,
  YearlyPayment,
} from "../interfaces";

import { AdviceProductDetailsFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import { BackendProductInfo } from "Components/Features/CheckPrice/Products/productConstants";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { isDefined } from "Utils/functions";
import { mapFormToDisabilityPriceRequest } from "./disabilityPriceRequestMapper";

export interface OldPriceCalculationRequest {
  productPriceUrl: string;
  insuranceName: INSURANCE_NAME_VALUE;
  data: { [key: string]: any };
}

export function mapFormToProductPriceRequest(
  formValues: AdviceProductDetailsFormValues | undefined,
  insuranceName: ADVICE_NAMES,
): OldPriceCalculationRequest {
  if (!isDefined(formValues)) {
    throw Error("Unable to map form to product price request for insurance");
  }

  let productPriceUrl;
  let data = {};
  switch (insuranceName) {
    case INSURANCE_NAME_VALUE.DISABILITY_FLEX:
      productPriceUrl = BackendProductInfo.DISABILITY.priceReqUrl;
      data = mapFormToDisabilityPriceRequest(formValues);
      break;
    case INSURANCE_NAME_VALUE.DEATH:
      productPriceUrl = BackendProductInfo.DEATH.priceReqUrl;
      break;
    case INSURANCE_NAME_VALUE.CRITICAL_ILLNESS:
      productPriceUrl = BackendProductInfo.CRITICAL_ILLNESS.priceReqUrl;
      break;
    default:
      throw Error(
        `Unable to map form to product price request for insurance: ${insuranceName}`,
      );
  }
  return { productPriceUrl, insuranceName, data };
}

export const getAdvisorAdviceValues = (
  payment?: SinglePayment | YearlyPayment,
): AdviceValues => {
  const maxPayoutRestriction =
    payment?.restrictions?.maxPayouts?.PRODUCT_MANAGER ?? 0;

  const minPayoutRestriction = payment?.restrictions?.minPayment ?? 0;

  const minAdviceValue = replaceValueOutOfScope(
    payment?.advice?.minValid ?? 0,
    minPayoutRestriction,
    maxPayoutRestriction,
  );

  const maxAdviceValue = replaceValueOutOfScope(
    payment?.advice?.maxValid ?? 0,
    minPayoutRestriction,
    maxPayoutRestriction,
  );

  const adviceValue = replaceValueOutOfScope(
    payment?.advice?.recommended ?? 0,
    minPayoutRestriction,
    maxPayoutRestriction,
  );

  return {
    adviceValue,
    minAdviceValue,
    maxAdviceValue,
  };
};

export const replaceValueOutOfScope = (
  value: number,
  min: number,
  max: number,
): number => {
  if (value < min) {
    return min;
  } else if (value > max) {
    return max;
  }
  return value;
};

interface AdviceValues {
  adviceValue: number;
  minAdviceValue: number | null;
  maxAdviceValue: number | null;
}

export const getCorrectAdviceValues = (
  payment?: SinglePayment | YearlyPayment,
  isAdvisor = false,
): AdviceValues => {
  if (isAdvisor) {
    return getAdvisorAdviceValues(payment);
  }
  return getCorrectCustomerAdviceValues(payment);
};

function getCorrectCustomerAdviceValues(
  payment?: SinglePayment | YearlyPayment,
): AdviceValues {
  const recommendation = payment?.recommendationConfiguration.recommendation;
  const maxPayoutCustomer = payment?.restrictions?.maxPayouts.CUSTOMER;
  if (recommendation === PurchaseRecommendation.PURCHASE_FROM_ADVISOR) {
    return {
      adviceValue: maxPayoutCustomer ?? 0,
      minAdviceValue: maxPayoutCustomer ?? 0,
      maxAdviceValue: maxPayoutCustomer ?? 0,
    };
  } else if (recommendation === PurchaseRecommendation.NOT_RECOMMENDED) {
    return {
      adviceValue: payment?.restrictions?.minPayment ?? 0,
      minAdviceValue: null,
      maxAdviceValue: null,
    };
  }
  const adviceValue = payment?.advice?.recommended ?? 0;
  const maxValid = payment?.advice?.maxValid;
  let maxAdviceValue = adviceValue;
  if (maxPayoutCustomer && maxValid) {
    maxAdviceValue =
      maxPayoutCustomer < maxValid ? maxPayoutCustomer : maxValid;
  }
  return {
    adviceValue,
    minAdviceValue: payment?.advice?.minValid ?? adviceValue,
    maxAdviceValue,
  };
}
